import { z } from "zod";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

type BikePrefsInput = {
    "isPersonalBike":        boolean;
    "bikeSpeedKmh":          15 | 25 | 45;
    "elevationCosts":        "LOW" | "NONE" | "HIGH";
    "transportPersonalBike": boolean;
    "endItineraryByBike":    boolean;
};

/**
 * Normalizes dependent bike flags after parse (personal bike speed and transport flags).
 *
 * @param params Raw fields before coupling rules.
 * @returns Coherent bike preference object for routing and UI.
 */
const normalizeBikePrefs = ( params: BikePrefsInput ): BikePrefsInput => {
    const isPersonalBike = params.isPersonalBike;

    const bikeSpeedKmh = isPersonalBike ? params.bikeSpeedKmh : 25;
    const transportPersonalBike = isPersonalBike === true && params.transportPersonalBike === true;

    return {
        ...params,
        bikeSpeedKmh,
        transportPersonalBike,
    };
};

/**
 * Zod schema for bike-related user preferences.
 *
 * @remarks The `.transform` enforces coupling rules for personal bike and transport flags.
 */
const BikePrefsSchema = z.object( {
    "isPersonalBike": z.boolean().default( false ),
    "bikeSpeedKmh":   z.union( [ z.literal( 15 ), z.literal( 25 ), z.literal( 45 ) ] ).
        catch( 25 ).
        default( 25 ),
    "elevationCosts":        z.enum( [ "LOW", "NONE", "HIGH" ] ).default( "NONE" ),
    "transportPersonalBike": z.boolean().default( false ),
    "endItineraryByBike":    z.boolean().default( false ),
} ).transform( normalizeBikePrefs );

/** In-memory and persisted shape for bike preferences. */
export type BikePrefs = z.infer<typeof BikePrefsSchema>;

/** Parsed defaults used before hydration and as decode fallback. */
export const BIKE_PREFS_DEFAULT: BikePrefs = BikePrefsSchema.parse( {} );

/**
 * Migrates v1 bike prefs: renames `tripEndByBike` → `endItineraryByBike`.
 *
 * @param data Unknown on-disk payload at version 1.
 * @returns Payload shape expected by the v2 schema.
 */
const migrateBikePrefsV1ToV2 = ( data: unknown ): unknown => {
    if ( data === null || typeof data !== "object" || Array.isArray( data ) ) {
        return data;
    }

    const record = { ...data as Record<string, unknown> };
    if ( record.endItineraryByBike === undefined && typeof record.tripEndByBike === "boolean" ) {
        record.endItineraryByBike = record.tripEndByBike;
    }
    delete record.tripEndByBike;

    return record;
};

/** Persisted slice: bike mode, speed, elevation, transport flags. */
export const bikePrefsSlice = definePersistedSlice<BikePrefs>( {
    "key":            "bikePrefs",
    "currentVersion": 2,
    "storage":        "preferences",
    "eager":          true,
    "schema":         BikePrefsSchema,
    "default":        () => BIKE_PREFS_DEFAULT,
    "migrations":     { "2": migrateBikePrefsV1ToV2 },
} );
